/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import {
  ActionInvokeCloudFunctionItem,
  defaultValue,
} from './ActionInvokeCloudFunction';

const ActionInvokeCloudFunctionManager: ActionManagerFn<
  'ActionInvokeCloudFunction'
> = (platform: Platform): ActionManager<'ActionInvokeCloudFunction'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionInvokeCloudFunction'),
    getDisplayText: (action) => action.props.functionUri ?? '',
    isChildAllowed: (page, item, childType) =>
      ['ActionOnResponse', 'ActionSetParam'].includes(childType),
    defaultValue,
    defaultChildren: ['ActionSetParam', 'ActionSetParam'],
    props: {
      ...baseActionProps,
      fnAppUid: {
        label: 'App',
        type: 'AppUidChooser',
        helpText: (item: ActionInvokeCloudFunctionItem) => {
          if (item.props.fnAppUid === platform.appUid) {
            return 'Warning! When you migrate this page to another app, it will invoke the function in this app. Leave this field blank otherwise.';
          }
          return 'Leave blank to use the current app. The function in the selected app will be invoked.';
        },
      },
      functionUri: {
        label: 'Function',
        type: 'FunctionURIChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
        getAppUid(page, item, context) {
          return (item as ActionInvokeCloudFunctionItem).props.fnAppUid;
        },
      },
      fileDownload: {
        label: 'For File Download',
        type: 'boolean',
        helpText: `Enable this if your cloud function is used for file download.

e.g. Cloud Function
\`\`\`javascript
// set the file name on the header
response.header("Content-Disposition", 'attachment; filename="test.csv"');
// allow it to be read by this action
response.header("Access-Control-Expose-Headers", 'Content-Disposition');
// set the file content
response.contentType("application/csv");
response.body("...");
\`\`\`
`,
      },
      openInNewTab: {
        label: 'Open In New Tab',
        type: 'boolean',
        helpText: 'Enable this to open the function in New Tab.',
      },
      fileNameExpression: {
        label: 'File Name',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          "File name to be used when it's not available on the response's Content-Disposition header.",
      },
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'fileNameExpression':
          return !!props.fileDownload;
        default:
          return true;
      }
    },
    onPropertyChange: (property, value) => {
      if (property === 'fnAppUid') {
        callback();
        return {
          [property]: value,
          functionUri: '',
        };
      }
      if (property === 'openInNewTab') {
        callback();
        return {
          [property]: value,
          fileDownload: false,
        };
      }
      if (property === 'fileDownload') {
        callback();
        return {
          [property]: value,
          openInNewTab: false,
        };
      }
      return {
        [property]: value,
      };
    },
    replaceableWith: ['ActionInvokeControllerFunction'],
    name: 'Invoke Cloud Function',
    description: `**Overview**<br/>
The Invoke Cloud Function action is a server-side integration component designed to execute a secure, custom Cloud Function hosted on the platform. It bridges the client-side UI and backend logic so the app can run complex processing, database operations, or third-party API calls away from the browser.<br/><br/>
This component acts as a logic container: it uses ActionSetParam children to build the JSON payload sent to the server, and ActionOnResponse children to handle the returned data. It also supports special modes for file downloads and opening results in a new tab.

**Key Features**<br/>
* **Secure Backend Execution:** Runs logic on the server to keep sensitive algorithms and secrets (like API keys) out of the browser.
* **Payload Transmission:** Uses ActionSetParam children to construct a JSON request body (request.body) for the function.
* **Response Handling:** Exposes the server response via $._.response, enabling post-processing through ActionOnResponse children.
* **Cross-Application Invocation:** Supports invoking a function from another application via fnAppUid.
* **Download Support:** Includes a fileDownload mode for handling binary responses and download-related headers (e.g., Content-Disposition).

**Properties**<br/>
* **App (fnAppUid):** AppUidChooser identifying the application where the function lives.
  * *Behavior:* Defaults to the current application when blank.
* **Function (functionUri) (Required):** FunctionURIChooser selecting the specific function to execute.
* **For File Download (fileDownload):** Boolean toggle to treat the response as a downloadable file.
  * *Conflict:* Disables Open In New Tab when enabled.
* **Open In New Tab:** Boolean toggle to open the function result in a new browser tab.
  * *Conflict:* Disables For File Download when enabled.
* **File Name:** Expression fallback filename.
  * *Visibility:* Only visible when fileDownload is true.
  * *Purpose:* Used when the server response doesn’t provide a filename.

**Use Cases**<br/>
* **Complex Business Logic:** Send order details to compute taxes, shipping, and discounts, then return the final totals.
* **Report Generation:** Generate a PDF/CSV on the server and return it as a download.
* **External Integrations:** Invoke server-side logic that calls payment/CRM APIs securely.

    **Sample Cloud Function**<br/>
    \`\`\`javascript
    // all the parameters set using \`Set Param\` action will be available under request.body
    const { param1, param2 } = request.body;

    // respond back with any JSON Object
    response.json({cfParam1: "Hello", cfParam2: "World"});
    \`\`\`

    **On Response**<br/>

    Use \`On Response\` child action to perform further actions after receiving the response from the Cloud Function call.

    \`\`\`javascript
    // The JSON Object from the server will be available as $._.response
    // Sample expression using the above values sent by the server
    \`\${$._.response.cfParam1} \${$._.response.cfParam2}!\` // Hello World!
    \`\`\``,
    icon: { iconName: 'bolt' },
  };
};

export default ActionInvokeCloudFunctionManager;
